import { app, nativeWindow, platform, screen, webContents } from '@todesktop/client-core';

export const desktopFloatUrls = ['/desktop-quick-note', '/desktop-global-search'];

import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { mutate } from 'swr';
import { shallow } from 'zustand/shallow';

import useAuthStore from './auth';

import { pick } from '@fabric-app/utils/object/manipulation';
import { isObject } from '@fabric-app/utils/guards';
import { appRoutes } from '@fabric-app/business/app/routing/routes.config';
import { useWoody } from '@fabric-app/business/woody/woody';
import { authApi } from '../modules/auth/api/auth.api';
import { toast } from '@fabric-app/business/store/alerts';

export function isInDesktop() {
  if (typeof window === 'undefined') return false;

  return platform.todesktop.isDesktopApp();
}

const ports = [23435, 39213];

type CommMessage =
  | {
      type: 'open-session';
      sessionId: string;
    }
  | {
      type: 'authenticate';
      token: string;
    };

export const isDesktopAppCommServerRunning = async () => {
  const { checkIfCommServerRunning } = await import('@todesktop/client-comm-server');

  return (await checkIfCommServerRunning(ports)) ? true : false;
};

const sendCommMessage = async (message: CommMessage): Promise<boolean> => {
  if (isInDesktop()) return false;

  const hasDesktop = await isDesktopAppCommServerRunning();

  if (!hasDesktop) return false;

  const { createBroadcastService } = await import('@todesktop/client-comm-server');

  const { broadcast } = createBroadcastService(ports);

  const response = await broadcast(message);

  return !!response?.success;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useOpenInDesktop = () => {
  const router = useRouter();

  return useCallback(
    async (sessionId: string) => {
      const success = await sendCommMessage({ type: 'open-session', sessionId });

      if (success) {
        await router.push(`/handoff/${sessionId}`);
      }
    },
    [router],
  );
};

export const useHandleDesktopAuthenticate = () => {
  const { client } = useWoody();
  const router = useRouter();
  const { setAuthStatus } = useAuthStore((state) => pick(state, ['setAuthStatus']), shallow);

  const handleAuthenticate = async (token: string) => {
    const response = await authApi.getSessionWithClaimToken(token);

    if (!response) return;

    try {
      const response = client.v2('/v2/user/me');

      mutate('woody-user', response, false);
      setAuthStatus('authenticated');

      window.location.href = appRoutes.home; // forces a refresh
      await nativeWindow.show();
      await app.focus({
        steal: true,
      });
    } catch {
      router.replace('/signin');
      toast({
        content: 'Failed to authenticate. Please try again.',
      });
      return;
    }
  };

  return handleAuthenticate;
};

export const useAuthenticateDesktop = () => {
  return useCallback(async () => {
    const token = await authApi.getClaimSessionToken();

    if (token) {
      const success = await sendCommMessage({
        type: 'authenticate',
        token,
      });

      return success;
    }

    return false;
  }, []);
};

export const useAuthenticateDesktopDeepLink = () => {
  return useCallback(async () => {
    const token = await authApi.getClaimSessionToken();
    if (token) {
      window.location.href = `fabric://authenticate?token=${token}`;
      return;
    } else {
      return false;
    }
  }, []);
};

export const useHandleDeeplink = () => {
  const router = useRouter();
  const { client } = useWoody();

  const handleAuthenticate = useHandleDesktopAuthenticate();

  useEffect(() => {
    if (!isInDesktop() || desktopFloatUrls.includes(router.pathname)) return;

    type Cb = Parameters<typeof app.on>[1];
    /**
     * the CB is typed that way now so... defining this value to make the type checker happy
     */
    const retValue = Promise.resolve(Promise.resolve);

    const handleOpenProtocolURL: Cb = (_, event) => {
      if (!isObject(event) || typeof event.url !== 'string') return retValue;

      const url = new URL(event.url);

      if (!['/authenticate', '/home/authenticate'].includes(url.pathname)) {
        return retValue;
      }

      if ('preventDefault' in event && event.preventDefault instanceof Function)
        event.preventDefault?.();

      const token = url.searchParams.get('token');
      if (!token) {
        router.replace('/signin');
        toast({
          content: 'Failed to authenticate. Please try again.',
        });
        return retValue;
      }

      handleAuthenticate(token);

      return retValue;
    };

    const unmounted = false;
    let unsubscribe: () => void;
    app.on('*', handleOpenProtocolURL).then((unsub) => {
      if (unmounted) {
        unsub();
        return;
      }

      unsubscribe = unsub;
    });

    return () => {
      if (!unsubscribe) return;
      unsubscribe();
    };
  }, [client, router.pathname, router.push, handleAuthenticate]);
};

export const useHandleBroadcast = () => {
  const router = useRouter();
  const { client } = useWoody();

  const handleAuthenticate = useHandleDesktopAuthenticate();

  useEffect(() => {
    let unsubscribe: () => void;
    let wasUnmounted = false;
    if (!isInDesktop() || desktopFloatUrls.includes(router.pathname)) return;

    import('@todesktop/client-comm-server').then(({ createBroadcastService }) => {
      if (wasUnmounted) return;

      const { handleBroadcast } = createBroadcastService(ports);

      const handleOpenSession = async (sessionId: string) => {
        await router.push(`/b/${sessionId}`);
        await nativeWindow.show();
        await app.focus({
          steal: true,
        });
      };

      try {
        unsubscribe = handleBroadcast((_message) => {
          // The message is an object, but it's not typed as such. We need to cast it separate from the
          // function parameters to avoid a type error.
          const message = _message as CommMessage;

          if (message.type === 'open-session') {
            handleOpenSession(message.sessionId);
          }

          if (message.type === 'authenticate') {
            handleAuthenticate(message.token);
          }

          return 'acknowledged';
        });
      } catch {
        console.error('Failed to handle broadcast message');
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
      wasUnmounted = true;
    };
  }, [client, router, router.pathname, router.push, handleAuthenticate]);
};

export const useOpenOnBrowser = () => {
  return useCallback(async (url: string) => {
    if (!isInDesktop()) return;

    await platform.os.openURL(url);
  }, []);
};

export const openQuickNoteWindow = async () => {
  if (!window.loggedIn || window.authStatus === 'unauthenticated') {
    return;
  }

  // Open /desktop-quick-note
  const url = new URL('/desktop-quick-note', window.location.origin).toString();

  const screenPoint = await screen.getCursorScreenPoint();
  const desktopPoint = await screen.getDisplayNearestPoint(screenPoint);

  // create a floating window with no frame and background
  // in the center of the screen
  const winRef = await nativeWindow.create({
    width: desktopPoint.bounds.width,
    height: desktopPoint.bounds.height,
    x: desktopPoint.bounds.x,
    y: desktopPoint.bounds.y,
    frame: false,
    show: false,
    transparent: true,
    alwaysOnTop: true,
    center: true,
    // fullscreen: true,
    webPreferences: {
      sandbox: false,
    },
  });

  await webContents.loadURL({ ref: winRef }, url);

  // if it loses focus, close it
  nativeWindow.on(
    'blur',
    async () => {
      await nativeWindow.close({ ref: winRef });
    },
    { ref: winRef },
  );

  // open the window
  await nativeWindow.show({ ref: winRef });
};

export const openDesktopGlobalSearch = async () => {
  if (!window.loggedIn || window.authStatus === 'unauthenticated') return;

  // Open /desktop-quick-note
  const url = new URL('/desktop-global-search', window.location.origin).toString();

  const screenPoint = await screen.getCursorScreenPoint();
  const desktopPoint = await screen.getDisplayNearestPoint(screenPoint);

  // create a floating window with no frame and background
  // in the center of the screen
  const winRef = await nativeWindow.create({
    width: desktopPoint.bounds.width,
    height: desktopPoint.bounds.height,
    x: desktopPoint.bounds.x,
    y: desktopPoint.bounds.y,
    frame: false,
    show: false,
    transparent: true,
    alwaysOnTop: true,
    hasShadow: false,
    center: true,
    vibrancy: 'hud',
    webPreferences: {
      sandbox: false,
      nodeIntegration: true,
    },
  });

  await webContents.loadURL({ ref: winRef }, url);

  // if it loses focus, close it
  nativeWindow.on(
    'blur',
    async () => {
      await nativeWindow.close({ ref: winRef });
    },
    { ref: winRef },
  );

  // open the window
  await nativeWindow.show({ ref: winRef });
};
